import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SubscriptionAgreementModal from '../components/SubscriptionAgreementModal';
import PublicPageFooter from '../components/PublicPageFooter';

interface SignatureData {
  signerName: string;
  signerEmail: string;
  signerTitle: string;
  signatureData: string;
  agreementVersion: string;
}

const SignupPage: React.FC = () => {
  const [formData, setFormData] = useState({
    business_name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    website: '',
    company_description: '',
    business_type: '',
    license_number: '',
    insurance_provider: '',
    insurance_policy_number: '',
    years_in_business: '',
    number_of_employees: '',
    physical_address: '',
    physical_city: '',
    physical_state: '',
    physical_zip: '',
    mailing_same_as_physical: true,
    mailing_address: '',
    mailing_city: '',
    mailing_state: '',
    mailing_zip: '',
    work_radius_miles: '25',
    devices: {
      ios: false,
      android: false,
      web: true,
      desktop: false
    },
    role: 'worker',
    terms_accepted: false
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [_logoPreview, setLogoPreview] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [_pendingSignatureData, setPendingSignatureData] = useState<SignatureData | null>(null);
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    });
  };

  const _handleDeviceChange = (device: 'ios' | 'android' | 'web' | 'desktop') => {
    setFormData({
      ...formData,
      devices: {
        ...formData.devices,
        [device]: !formData.devices[device]
      }
    });
  };

  const _handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Step 1: Validate form and show agreement modal
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!formData.terms_accepted) {
      setError('You must accept the terms and conditions');
      return;
    }

    // Validate required fields
    if (!formData.business_name.trim()) {
      setError('Legal business name is required');
      return;
    }
    if (!formData.phone.trim()) {
      setError('Business phone number is required');
      return;
    }
    if (!formData.business_type) {
      setError('Please select a business type');
      return;
    }

    // Show the agreement modal for signing
    setShowAgreementModal(true);
  };

  // Step 2: After agreement is signed, complete the registration
  const handleAgreementSigned = async (signatureData: SignatureData) => {
    setPendingSignatureData(signatureData);
    setShowAgreementModal(false);
    setIsLoading(true);

    try {
      // Prepare address data
      const physicalAddress = `${formData.physical_address}, ${formData.physical_city}, ${formData.physical_state} ${formData.physical_zip}`.trim();
      const mailingAddress = formData.mailing_same_as_physical
        ? physicalAddress
        : `${formData.mailing_address}, ${formData.mailing_city}, ${formData.mailing_state} ${formData.mailing_zip}`.trim();

      // Get primary device
      const devicePreference = Object.keys(formData.devices).find(key => formData.devices[key as keyof typeof formData.devices]) || 'web';

      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('business_name', formData.business_name);
      submitData.append('email', formData.email);
      submitData.append('password', formData.password);
      submitData.append('phone', formData.phone);
      submitData.append('website', formData.website);
      submitData.append('company_description', formData.company_description);
      submitData.append('business_type', formData.business_type);
      submitData.append('license_number', formData.license_number);
      submitData.append('insurance_provider', formData.insurance_provider);
      submitData.append('insurance_policy_number', formData.insurance_policy_number);
      submitData.append('years_in_business', formData.years_in_business);
      submitData.append('number_of_employees', formData.number_of_employees);
      submitData.append('city', formData.physical_city);
      submitData.append('state', formData.physical_state);
      submitData.append('zip_code', formData.physical_zip);
      submitData.append('home_address', physicalAddress);
      submitData.append('mailing_address', mailingAddress);
      submitData.append('work_radius_miles', formData.work_radius_miles);
      submitData.append('device_preference', devicePreference);
      submitData.append('role', formData.role);
      submitData.append('terms_accepted', formData.terms_accepted.toString());

      // Add agreement signature data
      submitData.append('agreement_signer_name', signatureData.signerName);
      submitData.append('agreement_signer_email', signatureData.signerEmail);
      submitData.append('agreement_signer_title', signatureData.signerTitle);
      submitData.append('agreement_signature_data', signatureData.signatureData);
      submitData.append('agreement_version', signatureData.agreementVersion);

      // Add logo file if exists
      if (logoFile) {
        submitData.append('logo', logoFile);
      }

      const response = await fetch('/api/auth/register', {
        method: 'POST',
        body: submitData
        // Don't set Content-Type header - browser will set it with boundary for multipart/form-data
      });

      if (response.ok) {
        await response.json();
        alert('Registration successful! Please check your email to verify your account.');
        navigate('/login');
      } else {
        const data = await response.json();
        setError(data?.error || 'Registration failed');
      }
    } catch (err) {
      setError('An error occurred during registration');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Create your ProBizPay account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Join ProBizPay to manage your service business
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="space-y-4">
            {/* Business Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium text-gray-900">Business Information</h3>

              <div>
                <input
                  id="business_name"
                  name="business_name"
                  type="text"
                  required
                  className="input-field"
                  placeholder="Legal Business Name *"
                  value={formData.business_name}
                  onChange={handleChange}
                />
              </div>

              <div>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  required
                  className="input-field"
                  placeholder="Business Phone Number *"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>

              <div>
                <select
                  id="business_type"
                  name="business_type"
                  required
                  className="input-field"
                  value={formData.business_type}
                  onChange={handleChange}
                >
                  <option value="">Select Business Type *</option>
                  <option value="General Contractor">General Contractor</option>
                  <option value="Plumbing">Plumbing</option>
                  <option value="Electrical">Electrical</option>
                  <option value="HVAC">HVAC</option>
                  <option value="Roofing">Roofing</option>
                  <option value="Landscaping">Landscaping</option>
                  <option value="Painting">Painting</option>
                  <option value="Carpentry">Carpentry</option>
                  <option value="Flooring">Flooring</option>
                  <option value="Drainage">Drainage</option>
                  <option value="Concrete">Concrete</option>
                  <option value="Handyman">Handyman</option>
                  <option value="Cleaning">Cleaning</option>
                  <option value="Pest Control">Pest Control</option>
                  <option value="Pool Service">Pool Service</option>
                  <option value="Tree Service">Tree Service</option>
                  <option value="Fencing">Fencing</option>
                  <option value="Demolition">Demolition</option>
                  <option value="Excavation">Excavation</option>
                  <option value="Septic">Septic</option>
                  <option value="Irrigation">Irrigation</option>
                  <option value="Solar">Solar</option>
                  <option value="Service Company">Service Company</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <p className="text-sm text-gray-500">Additional business details (licensing, insurance, service areas) can be completed during onboarding after signup.</p>

              {/* Hidden fields to preserve form data compatibility */}
              <input type="hidden" name="physical_city" value={formData.physical_city} />
              <input type="hidden" name="physical_state" value={formData.physical_state} />

              {/* Removed from signup, moved to onboarding:
                  - Logo, Website, Description, License, Insurance
                  - Years in Business, Employees, Address, Work Radius
              */}
            </div>

            {/* Account Credentials */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-lg font-medium text-gray-900">Account Credentials</h3>

              <div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="input-field"
                  placeholder="Email address *"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              <div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="input-field"
                  placeholder="Password *"
                  value={formData.password}
                  onChange={handleChange}
                />
                <p className="mt-1 text-xs text-gray-500">
                  Must be 8+ characters with uppercase, lowercase, number, and special character
                </p>
              </div>

              <div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  className="input-field"
                  placeholder="Confirm password *"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="flex items-start pt-4">
              <input
                id="terms_accepted"
                name="terms_accepted"
                type="checkbox"
                required
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                checked={formData.terms_accepted}
                onChange={handleChange}
              />
              <label htmlFor="terms_accepted" className="ml-2 block text-sm text-gray-900">
                I accept the{' '}
                <a href="/terms" className="text-blue-600 hover:text-blue-500" target="_blank">
                  Terms and Conditions
                </a>{' '}
                and{' '}
                <a href="/privacy" className="text-blue-600 hover:text-blue-500" target="_blank">
                  Privacy Policy
                </a>
                <span className="text-red-600"> *</span>
              </label>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full"
            >
              {isLoading ? 'Creating account...' : 'Create Account'}
            </button>
          </div>

          <div className="text-center">
            <span className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
                Sign in
              </Link>
            </span>
            <p className="mt-1 text-xs text-gray-500 italic">
              Using the ProBizPay app? Tap "Done" at the top to return and sign in there.
            </p>
          </div>
        </form>

        <PublicPageFooter />
      </div>

      {/* Subscription Agreement Modal */}
      <SubscriptionAgreementModal
        isOpen={showAgreementModal}
        onClose={() => setShowAgreementModal(false)}
        onSigned={handleAgreementSigned}
        userEmail={formData.email}
        userName={formData.business_name}
        mode="signup"
      />
    </div>
  );
};

export default SignupPage;
